import {
  IBaseDateChartDataRange,
  IBaseDateChartDataValue,
  IDateChartData,
  IDateChartDataRange,
} from '@app/api/interfaces/chart-data/base-date-chart-data';
import { IDateRange } from '@app/core/date-range/interfaces/date-range.interface';
import { convertStringInToSumOfAllCharacters } from '@app/shared/utils/string';
import { generateDateRangeReverse } from '@mock/utils/date-helper';

enum CompareDatesMode {
  Less = 'less',
  Greater = 'greater',
}

export function generateDateDateChartData<
  IChartDataValue extends IBaseDateChartDataValue,
  IChartDataRange extends IBaseDateChartDataRange = IBaseDateChartDataRange,
>(
  endDate: string,
  dateChartDataRange: (date: string, index: number) => IChartDataRange,
  eachValueGenerator: (date: string, index: number) => IChartDataValue,
  ignoreMonth: number[] = []
): IDateChartData<IChartDataValue, IChartDataRange> {
  const allDateRangeLength = 700;
  const limitationForDateRangeLength = 600;
  const { deploymentId, serialNo } = dateChartDataRange('', 0);

  const changeDeploymentInDays = convertStringInToSumOfAllCharacters(deploymentId) % 60 || 60;
  let allDateRange = convertStringInToSumOfAllCharacters(deploymentId) % allDateRangeLength || allDateRangeLength;
  allDateRange = allDateRange < limitationForDateRangeLength ? limitationForDateRangeLength : allDateRange;

  const ignoreMonthForDate = ignoreMonth.map(m => `-${m < 10 ? '0' : ''}${m}-`);
  // exclude some data for showing empty bars in the chart (remove december from data )
  const dates = generateDateRangeReverse(endDate, allDateRange).filter(
    el => !ignoreMonthForDate.some(month => el.includes(month))
  );

  const chunkedDates: string[][] = [];

  for (let i = 0; i < dates.length; i += changeDeploymentInDays) {
    const chunk = dates.slice(i, i + changeDeploymentInDays);
    chunkedDates.push(chunk);
  }

  const ranges: IDateChartDataRange<IChartDataValue, IChartDataRange>[] = chunkedDates.map((dateChunk, index) => {
    return {
      ...dateChartDataRange(dateChunk[0], index),
      deployed: dateChunk[0],
      collected: dateChunk[0],
      deploymentId: index === 0 ? deploymentId : deploymentId + '_' + index,
      serialNo: serialNo,
      values: dateChunk.map((date, valueIndex) => eachValueGenerator(date, valueIndex)),
    } as IDateChartDataRange<IChartDataValue, IChartDataRange>;
  });

  return { ranges };
}

export function sortByDate<
  IChartDataValue extends IBaseDateChartDataValue,
  IChartDataRange extends IBaseDateChartDataRange = IBaseDateChartDataRange,
>(
  data: IDateChartData<IChartDataValue, IChartDataRange>,
  dateRange: IDateRange
): IDateChartData<IChartDataValue, IChartDataRange> {
  const newData: IDateChartData<IChartDataValue, IChartDataRange> = { ranges: [] };

  data.ranges.forEach(range => {
    const filteredValues: IChartDataValue[] = range.values.filter(value => {
      const isAfterStartDate = dateRange.startDate
        ? compareDates(value.date, dateRange.startDate, CompareDatesMode.Greater, true)
        : true;
      const isBeforeEndDate = dateRange.endDate
        ? compareDates(value.date, dateRange.endDate, CompareDatesMode.Less, true)
        : true;

      return isAfterStartDate && isBeforeEndDate;
    });

    if (filteredValues.length > 0) {
      newData.ranges.push({
        ...range,
        values: [...filteredValues],
      });
    }
  });

  return newData;
}

export const compareDates = (date1: string, date2: string, mode: CompareDatesMode, includeSame = false): boolean => {
  const d1 = new Date(date1).setHours(0, 0, 0, 0);
  const d2 = new Date(date2).setHours(0, 0, 0, 0);

  if (mode === CompareDatesMode.Less) {
    return includeSame ? d1 <= d2 : d1 < d2;
  }

  if (mode === CompareDatesMode.Greater) {
    return includeSame ? d1 >= d2 : d1 > d2;
  }

  return false;
};
